/**
 * The asset's materials, grouped by type. Everyone who reads the asset may preview (a PDF inline in a new tab,
 * Markdown in a dialog) and download; asset managers upload files, add links, edit, mark superseded and remove.
 * Superseded materials reach managers only, under "Older versions"; the server leaves them out for anyone else.
 */

import { useState } from 'react';
import { FolderOpen, Link2, Upload } from 'lucide-react';

import { useUpdateMaterial } from '@/api/queries/materials';
import type { AssetOut } from '@/api/types';
import type { MaterialOut } from '@/api/types/materials';
import { useSession } from '@/session/SessionContext';
import { FormError, StateCard } from '@/ui/StateCard';

import {
  AddLinkDialog,
  EditMaterialDialog,
  MarkdownPreviewDialog,
  RemoveMaterialDialog,
  SupersedeDialog,
  type MaterialWriter,
} from './MaterialDialogs';
import { MaterialRow, type MaterialAction } from './MaterialRow';
import { UploadDialog } from './UploadDialog';
import { groupMaterials, type MaterialGroup } from './materialList';
import { useRevision } from './useRevision';
import './catalog-tabs.css';

type Open =
  | { kind: 'upload' }
  | { kind: 'link' }
  | { kind: Exclude<MaterialAction, 'restore'>; material: MaterialOut };

function MaterialTable({
  caption,
  rows,
  assetId,
  manage,
  onAction,
}: {
  caption: string;
  rows: MaterialOut[];
  assetId: string;
  manage: boolean;
  onAction: (material: MaterialOut, action: MaterialAction) => void;
}) {
  return (
    <div className="table-scroll">
      <table className="data zebra compact" aria-label={caption}>
        <thead>
          <tr>
            <th scope="col">Title</th>
            <th scope="col">Audience</th>
            <th scope="col">File or link</th>
            <th scope="col"><span className="sr-only">Actions</span></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((m) => (
            <MaterialRow key={m.id} assetId={assetId} material={m} manage={manage} onAction={(a) => onAction(m, a)} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Group({ group, ...table }: { group: MaterialGroup; assetId: string; manage: boolean; onAction: (m: MaterialOut, a: MaterialAction) => void }) {
  return (
    <section className="catalog-tab-group" aria-labelledby={`materials-${group.type}`}>
      <h3 id={`materials-${group.type}`}>{group.label}</h3>
      {group.current.length > 0 ? (
        <MaterialTable caption={group.label} rows={group.current} {...table} />
      ) : (
        <p className="hint">Only older versions.</p>
      )}
      {table.manage && group.older.length > 0 && (
        <details className="catalog-tab-older">
          <summary>Older versions ({group.older.length})</summary>
          <MaterialTable caption={`${group.label}: older versions`} rows={group.older} {...table} />
        </details>
      )}
    </section>
  );
}

function Toolbar({ onOpen }: { onOpen: (open: Open) => void }) {
  return (
    <div className="catalog-tab-toolbar">
      <button type="button" className="btn btn-secondary btn-sm" onClick={() => onOpen({ kind: 'upload' })}>
        <Upload aria-hidden="true" /> Upload files
      </button>
      <button type="button" className="btn btn-secondary btn-sm" onClick={() => onOpen({ kind: 'link' })}>
        <Link2 aria-hidden="true" /> Add link
      </button>
    </div>
  );
}

function Dialogs({ open, writer, materials, onClose }: { open: Open | null; writer: MaterialWriter; materials: MaterialOut[]; onClose: () => void }) {
  if (!open) return null;
  switch (open.kind) {
    case 'upload':
      return <UploadDialog writer={writer} onClose={onClose} />;
    case 'link':
      return <AddLinkDialog writer={writer} onClose={onClose} />;
    case 'edit':
      return <EditMaterialDialog writer={writer} material={open.material} onClose={onClose} />;
    case 'remove':
      return <RemoveMaterialDialog writer={writer} material={open.material} onClose={onClose} />;
    case 'preview':
      return <MarkdownPreviewDialog assetId={writer.assetId} material={open.material} onClose={onClose} />;
    default: {
      const replacements = materials.filter((m) => !m.superseded_by && m.id !== open.material.id);
      return <SupersedeDialog writer={writer} material={open.material} replacements={replacements} onClose={onClose} />;
    }
  }
}

export function MaterialsTab({ asset }: { asset: AssetOut }) {
  const { can } = useSession();
  const manage = can('asset.manage');
  const materials = asset.materials;
  const { revision, saw } = useRevision(asset.revision);
  const writer: MaterialWriter = { assetId: asset.id, revision, saw };
  const restore = useUpdateMaterial(asset.id);
  const [open, setOpen] = useState<Open | null>(null);
  const groups = groupMaterials(materials);

  function onAction(material: MaterialOut, action: MaterialAction) {
    if (action !== 'restore') return setOpen({ kind: action, material });
    restore.mutate({ id: material.id, change: { revision, superseded_by: null } }, { onSuccess: saw });
  }

  return (
    <div className="stack-md">
      {manage && <Toolbar onOpen={setOpen} />}
      <FormError error={restore.error} />
      {groups.length === 0 ? (
        <StateCard icon={<FolderOpen aria-hidden="true" />} title="No materials yet">
          <p>{manage ? 'Upload files or add links to share decks, one-pagers and demos.' : 'Decks, one-pagers and demos appear here once they are added.'}</p>
        </StateCard>
      ) : (
        groups.map((g) => <Group key={g.type} group={g} assetId={asset.id} manage={manage} onAction={onAction} />)
      )}
      <Dialogs open={open} writer={writer} materials={materials} onClose={() => setOpen(null)} />
    </div>
  );
}
